import { useCallback, useEffect, useRef, useState } from "react";
import createSocket from "../../../helpers/createSocket";
import ShopeeV2Api, {
  createIdempotencyKey,
  getShopeeJobIds
} from "../../../services/shopeeV2";
import {
  isShopeeNewOrderEvent,
  SHOPEE_REALTIME_EVENT,
  shouldRefreshShopeeWorkspace
} from "./shopeeWorkspaceRealtime";

const REALTIME_REFRESH_DELAY_MS = 250;
const DEFAULT_FILTERS = { page: 1, page_size: 20, active_only: true };

const EMPTY_DATA = {
  summary: {
    sales: { gmv: 0, refunds: 0, shopee_fees: 0, net_revenue: 0, new_orders: 0, pending_orders: 0, expiring_orders: 0 },
    cash_flow: { released_payout: 0 },
    operations: { issues: 0 }
  },
  health: { status: "unknown" },
  issues: {
    orders: [],
    mappings: [],
    jobs: []
  },
  orders: [],
  mappings: [],
  returns: [],
  settlements: [],
  jobs: [],
  settings: null
};

const DEFAULT_CAPABILITIES = {
  shopee_shop: { read: true, write: true },
  shopee_order: { read: true, write: true },
  shopee_catalog: { read: true, write: true },
  shopee_inventory: { read: true, write: true },
  shopee_fulfillment: { read: true, write: true },
  shopee_return: { read: true, write: true },
  shopee_finance: { read: true, write: true },
  shopee_monitor: { read: true, write: true }
};

const errorMessage = (error, intl) => {
  const responseMessage =
    error &&
    error.response &&
    error.response.data &&
    error.response.data.error &&
    error.response.data.error.message;
  if (responseMessage) return responseMessage;
  if (
    error &&
    (error.code === "ECONNABORTED" || /timeout of \d+ms exceeded/i.test(error.message || ""))
  ) {
    return (
      (intl &&
        intl.formatMessage({ id: "shopee.workspace.error.no-response" })) ||
      "Yêu cầu Shopee không phản hồi. Vui lòng kiểm tra Redis và Shopee worker rồi thử lại."
    );
  }
  return (
    (error && error.message) ||
    (intl && intl.formatMessage({ id: "shopee.workspace.error.load-failed" })) ||
    "Không thể tải dữ liệu Shopee"
  );
};

export default function useShopeeWorkspace(intl) {
  const intlRef = useRef(intl);
  useEffect(() => {
    intlRef.current = intl;
  }, [intl]);
  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(true);
  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [selectedShopId, setSelectedShopId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [data, setData] = useState(EMPTY_DATA);
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selection, setSelection] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const jobTimersRef = useRef(new Set());
  const realtimeTimerRef = useRef(null);
  const loadRef = useRef(null);
  const selectedShopIdRef = useRef(selectedShopId);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      jobTimersRef.current.forEach(timer => clearInterval(timer));
      jobTimersRef.current.clear();
      if (realtimeTimerRef.current) clearTimeout(realtimeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingShops(true);
    const safetyTimeout = setTimeout(() => {
      setLoadingShops(false);
    }, 3500);
    Promise.all([
      ShopeeV2Api.shops(controller.signal).catch(err => {
        const isAbort =
          err.name === "CanceledError" ||
          err.name === "AbortError" ||
          err.code === "ERR_CANCELED";
        if (!isAbort) setError(errorMessage(err, intlRef.current));
        return { data: [] };
      }),
      ShopeeV2Api.capabilities(null, controller.signal).catch(() => ({ data: {} }))
    ])
      .then(([shopResponse, capabilityResponse]) => {
        const nextShops = shopResponse.data || [];
        setShops(nextShops);
        setCapabilities(capabilityResponse.data || {});
        if (nextShops.length > 0) {
          setSelectedShopId(current => {
            if (current === "ALL") return "ALL";
            if (current && nextShops.some(s => String(s.shop_id) === String(current))) return current;
            return "ALL";
          });
        } else {
          setSelectedShopId("");
        }
      })
      .catch(error => {
        const isAbort =
          error.name === "CanceledError" ||
          error.name === "AbortError" ||
          error.code === "ERR_CANCELED";
        if (!isAbort) setError(errorMessage(error, intlRef.current));
      })
      .finally(() => {
        clearTimeout(safetyTimeout);
        setLoadingShops(false);
      });
    return () => {
      clearTimeout(safetyTimeout);
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!selectedShopId) return undefined;
    const controller = new AbortController();
    ShopeeV2Api.capabilities(selectedShopId, controller.signal)
      .then(response => setCapabilities(response.data || {}))
      .catch(error => {
        const isAbort =
          error.name === "CanceledError" ||
          error.name === "AbortError" ||
          error.code === "ERR_CANCELED";
        if (!isAbort) setError(errorMessage(error, intlRef.current));
      });
    return () => controller.abort();
  }, [selectedShopId]);

  const load = useCallback(async () => {
    if (!selectedShopId) {
      if (mountedRef.current) setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      let response;
      if (activeTab === "overview") {
        const [summaryRes, healthRes, issuesRes, mappingsRes] = await Promise.allSettled([
          ShopeeV2Api.summary(selectedShopId, filters, controller.signal),
          ShopeeV2Api.health(selectedShopId, controller.signal),
          ShopeeV2Api.issues(selectedShopId, controller.signal),
          ShopeeV2Api.mappings(selectedShopId, filters, controller.signal)
        ]);
        if (!mountedRef.current || controller.signal.aborted) return;
        setData(current => ({
          ...current,
          summary: summaryRes.status === "fulfilled" ? summaryRes.value.data || {} : current.summary,
          health: healthRes.status === "fulfilled" ? healthRes.value.data || {} : current.health,
          issues: issuesRes.status === "fulfilled" ? issuesRes.value.data || {} : current.issues,
          mappings: mappingsRes.status === "fulfilled" ? mappingsRes.value.data || [] : current.mappings
        }));
        return;
      }
      if (activeTab === "orders" || activeTab === "fulfillment" || activeTab === "shipping") {
        response = await ShopeeV2Api.orders(
          selectedShopId,
          activeTab === "fulfillment" || activeTab === "shipping"
            ? { ...filters, status: "READY_TO_SHIP" }
            : filters,
          controller.signal
        );
        setData(current => ({ ...current, orders: response.data || [] }));
      } else if (["catalog", "products", "mappings", "inventory", "pricing"].includes(activeTab)) {
        response = await ShopeeV2Api.mappings(selectedShopId, filters, controller.signal);
        setData(current => ({ ...current, mappings: response.data || [] }));
      } else if (activeTab === "returns") {
        response = await ShopeeV2Api.returns(selectedShopId, filters, controller.signal);
        setData(current => ({ ...current, returns: response.data || [] }));
      } else if (activeTab === "finance" || activeTab === "reconciliation") {
        response = await ShopeeV2Api.settlements(selectedShopId, filters, controller.signal);
        setData(current => ({ ...current, settlements: response.data || [] }));
      } else if (activeTab === "issues") {
        response = await ShopeeV2Api.issues(selectedShopId, controller.signal);
        setData(current => ({ ...current, issues: response.data || null }));
      } else if (activeTab === "connections" || activeTab === "automation") {
        response = await ShopeeV2Api.settings(selectedShopId, controller.signal);
        setData(current => ({ ...current, settings: response.data || null }));
      } else if (activeTab === "logs") {
        response = await ShopeeV2Api.jobs(selectedShopId, filters, controller.signal);
        setData(current => ({ ...current, jobs: response.data || [] }));
      }
      if (!controller.signal.aborted && response) setMeta(response.meta || {});
    } catch (error) {
      const isAbort =
        error.name === "CanceledError" ||
        error.name === "AbortError" ||
        error.code === "ERR_CANCELED" ||
        (error && error.message === "canceled");
      if (!isAbort && !controller.signal.aborted && mountedRef.current) {
        setError(errorMessage(error, intlRef.current));
      }
    } finally {
      if (mountedRef.current && (abortRef.current === controller || !abortRef.current)) {
        setLoading(false);
      }
    }
  }, [activeTab, filters, selectedShopId]);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    selectedShopIdRef.current = selectedShopId;
  }, [selectedShopId]);

  useEffect(() => {
    setData(EMPTY_DATA);
    setMeta({});
    setSelection([]);
    setFilters(DEFAULT_FILTERS);
  }, [selectedShopId]);

  useEffect(() => {
    setSelection([]);
    setFilters(current => ({
      page: 1,
      page_size: current.page_size || 20,
      active_only: true,
      from: current.from || "",
      to: current.to || ""
    }));
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const mainEl = document.querySelector("main");
      if (mainEl) mainEl.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const watchJob = useCallback(
    jobId => {
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        try {
          const response = await ShopeeV2Api.job(jobId);
          const job = response.data;
          if (["completed", "failed", "dead"].includes(job.status) || attempts >= 120) {
            clearInterval(timer);
            jobTimersRef.current.delete(timer);
            const jobIntl = intlRef.current;
            setNotice(
              job.status === "completed"
                ? (jobIntl && jobIntl.formatMessage({ id: "shopee.workspace.notice.job-completed" })) || "Tác vụ Shopee đã hoàn tất"
                : ((jobIntl &&
                    jobIntl.formatMessage(
                      { id: "shopee.workspace.notice.job-ended-status" },
                      { status: job.status },
                    )) ||
                  `Tác vụ Shopee kết thúc ở trạng thái ${job.status}`)
            );
            load();
          }
        } catch (error) {
          if (attempts >= 3) {
            clearInterval(timer);
            jobTimersRef.current.delete(timer);
          }
        }
      }, 1500);
      jobTimersRef.current.add(timer);
      return timer;
    },
    [load]
  );

  useEffect(() => {
    const socket = createSocket();
    let hasConnected = false;
    const scheduleRefresh = () => {
      if (realtimeTimerRef.current) return;
      realtimeTimerRef.current = setTimeout(() => {
        realtimeTimerRef.current = null;
        if (mountedRef.current && typeof loadRef.current === "function") {
          loadRef.current();
        }
      }, REALTIME_REFRESH_DELAY_MS);
    };
    const handleDataChanged = event => {
      if (shouldRefreshShopeeWorkspace(event, selectedShopIdRef.current)) {
        if (isShopeeNewOrderEvent(event)) {
          const dataIntl = intlRef.current;
          setNotice(
            (dataIntl &&
              dataIntl.formatMessage({
                id: "shopee.workspace.notice.new-order",
              })) ||
              "Đã nhận đơn hàng mới từ Shopee"
          );
        }
        scheduleRefresh();
      }
    };
    const handleConnect = () => {
      if (hasConnected) scheduleRefresh();
      hasConnected = true;
    };
    socket.on("connect", handleConnect);
    socket.on(SHOPEE_REALTIME_EVENT, handleDataChanged);
    return () => {
      if (realtimeTimerRef.current) {
        clearTimeout(realtimeTimerRef.current);
        realtimeTimerRef.current = null;
      }
      socket.off("connect", handleConnect);
      socket.off(SHOPEE_REALTIME_EVENT, handleDataChanged);
      socket.disconnect();
    };
  }, []);

  const runAction = useCallback(
    async (key, action, message) => {
      if (busy[key]) return null;
      setBusy(current => ({ ...current, [key]: true }));
      setError("");
      try {
        const response = await action(createIdempotencyKey(key));
        const jobIds = getShopeeJobIds(response);
        const actionIntl = intlRef.current;
        setNotice(
          message ||
            (actionIntl && actionIntl.formatMessage({
              id: jobIds.length
                ? "shopee.workspace.notice.action-queued"
                : "shopee.workspace.notice.action-success",
            })) ||
            (jobIds.length ? "Đã đưa tác vụ vào hàng đợi" : "Đã cập nhật thành công")
        );
        if (jobIds.length) jobIds.forEach(watchJob);
        else load();
        return response;
      } catch (error) {
        setError(errorMessage(error, intlRef.current));
        return null;
      } finally {
        setBusy(current => ({ ...current, [key]: false }));
      }
    },
    [busy, load, watchJob]
  );

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");

  const connectShop = useCallback(async () => {
    setConnecting(true);
    setConnectError("");
    try {
      const response = await ShopeeV2Api.authorizationUrl();
      if (response && response.data && response.data.authorization_url) {
        window.location.assign(response.data.authorization_url);
        return response.data.authorization_url;
      }
    } catch (error) {
      const msg = errorMessage(error);
      setConnectError(msg);
      return null;
    } finally {
      setConnecting(false);
    }
  }, []);

  return {
    shops,
    loadingShops,
    capabilities,
    selectedShopId,
    setSelectedShopId,
    activeTab,
    setActiveTab,
    data,
    meta,
    filters,
    setFilters,
    selection,
    setSelection,
    loading,
    busy,
    error,
    notice,
    clearNotice: () => setNotice(""),
    load,
    runAction,
    connecting,
    connectError,
    connectShop
  };
}
